// Shape and helpers for dream companies (users/{uid}/dreamCompanies docs).
// Pure functions only, so they can be tested on their own.

import type { TargetRole } from "../components/addTargetRole";
import type { ContactPerson } from "../components/addContactPerson";
import { toStatus, type ApiItem } from "./applicationCards";

/** Most companies one user can follow. */
export const MAX_DREAM_COMPANIES = 12;

/** A board posting that matches the user's answers (backend/dream_tracking.py). */
export type OpenRole = {
  id: string;
  title: string;
  url: string;
  locations: string[];
  /** ISO, as the board reports it */
  firstPublished: string | null;
  /** ISO, when Caramel first saw it */
  firstSeenAt: string | null;
  /** Opened after the user started following (not part of the board's first check) */
  new: boolean;
  /** How it matched the user's level: "new_grad", "intern", "open" (title says no level), ... */
  level?: string | null;
  /** Years of experience the description asks for; null = not stated (backend/experience.py) */
  yearsRequired?: number | null;
};

/**
 * Written by the backend (board endpoint and job-board checker), except
 * `seenAt`, which the app sets when the user has looked at the company.
 */
export type DreamTracking = {
  /** The board these roles came from */
  boardKey?: string | null;
  /** Matching open roles, new first, then newest */
  openRoles?: OpenRole[];
  matchedAt?: any;
  /** ISO; new roles first seen before this are no longer "new" */
  seenAt?: string;
};

/** boards/{key}: what the card needs from the job board doc. */
export type DreamBoard = {
  ats?: string;
  companyName?: string;
  lastCheckedAt?: any; // Firestore timestamp
  lastStatus?: string;
};

/** One item on a company's next-steps list. */
export type Step = {
  id: string;
  text: string;
  done: boolean;
  createdAt: string; // ISO
  doneAt?: string; // ISO, when ticked off
};

/** Before the steps list: one next step, plus ticked-off ones. Read only, to convert. */
export type DoneStep = { text: string; doneAt: string /* ISO */ };

export type DreamCompany = {
  id: string;
  name: string;
  domain?: string;
  /** Next steps, open and done, in the order they were added */
  steps?: Step[];
  /** Older docs: a single next step and the ones ticked off. Read through stepsOf(). */
  nextStep?: string;
  doneSteps?: DoneStep[];
  notes?: string;
  roles?: TargetRole[];
  people?: ContactPerson[];
  /**
   * Job board the company posts on, e.g. "greenhouse:pinterest" (set by the
   * backend, boards.py). null = we looked and found none; missing = not checked yet.
   */
  boardKey?: string | null;
  tracking?: DreamTracking;
  createdAt: any;
};

// --- Next steps ------------------------------------------------------------------

/** The company's steps, converting the older nextStep / doneSteps fields. */
export function stepsOf(company: DreamCompany): Step[] {
  if (Array.isArray(company.steps)) return company.steps;
  const steps: Step[] = (company.doneSteps || []).map((step, i) => ({
    id: `done-${i}`,
    text: step.text,
    done: true,
    createdAt: step.doneAt,
    doneAt: step.doneAt,
  }));
  if (company.nextStep?.trim()) {
    steps.push({ id: "next", text: company.nextStep.trim(), done: false, createdAt: "" });
  }
  return steps;
}

export const openSteps = (steps: Step[]) => steps.filter((s) => !s.done);

/** Done steps, most recently finished first. */
export const doneSteps = (steps: Step[]) =>
  steps.filter((s) => s.done).sort((a, b) => (b.doneAt || "").localeCompare(a.doneAt || ""));

export function addStep(steps: Step[], text: string, now = new Date()): Step[] {
  const clean = text.trim();
  if (!clean) return steps;
  const id = `${now.getTime().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  return [...steps, { id, text: clean, done: false, createdAt: now.toISOString() }];
}

/** Tick off or untick. Unticking puts it back on the open list (at the end). */
export function setStepDone(steps: Step[], id: string, done: boolean, now = new Date()): Step[] {
  const step = steps.find((s) => s.id === id);
  if (!step) return steps;
  const rest = steps.filter((s) => s.id !== id);
  if (done) return [...rest, { ...step, done: true, doneAt: now.toISOString() }];
  const { doneAt, ...open } = step;
  return [...rest, { ...open, done: false }];
}

/** Change a step's text; empty text deletes it. */
export function editStep(steps: Step[], id: string, text: string): Step[] {
  const clean = text.trim();
  return clean ? steps.map((s) => (s.id === id ? { ...s, text: clean } : s)) : steps.filter((s) => s.id !== id);
}

// --- Company names -------------------------------------------------------------
// TypeScript twin of normalize_company in backend/matching.py — keep the two in sync.

const COMPANY_SUFFIXES = new Set([
  "inc", "incorporated", "llc", "ltd", "limited", "corp", "corporation",
  "co", "company", "plc", "gmbh", "ag", "sa", "lp", "llp",
  "technologies", "technology", "labs",
  "careers", "career", "jobs", "recruiting", "recruitment", "recruiter",
  "talent", "acquisition", "hiring", "team", "hr", "university",
]);

const DOMAIN_SUFFIX = /\.(com|io|ai|co|org|net|dev|app)\b/g;

/** "Google LLC" -> "google", "The Walt Disney Company" -> "waltdisney". "" for blank/unknown. */
export function normalizeCompany(name?: string | null): string {
  if (!name) return "";
  const ascii = name
    .toLowerCase()
    .replace(DOMAIN_SUFFIX, "")
    .normalize("NFKD")
    .replace(/[\u0080-\uffff]/g, "") // accents split off by NFKD, and anything else non-ASCII
    .replace(/&/g, " and ");
  let words = ascii.split(/[^a-z0-9]+/).filter(Boolean);
  if (words[0] === "the") words = words.slice(1);
  while (words.length > 1 && COMPANY_SUFFIXES.has(words[words.length - 1])) words.pop();
  const key = words.join("");
  return key === "unknown" ? "" : key;
}

// --- Role titles -----------------------------------------------------------------
// TypeScript twin of role_tokens / role_similarity / roles_match in
// backend/matching.py (what Gmail sync uses to tell roles apart) — keep in sync.

const ROLE_PHRASES: [RegExp, string][] = [
  [/\bsoftware development engineer\b/g, "software engineer"],
  [/\bco[\s-]?op\b/g, "coop"],
  [/\bnew[\s-]?grad(uate)?\b/g, "newgrad"],
  [/\bfull[\s-]?time\b/g, "fulltime"],
];

const ROLE_SYNONYMS: Record<string, string[]> = {
  swe: ["software", "engineer"],
  sde: ["software", "engineer"],
  pm: ["product", "manager"],
  tpm: ["technical", "program", "manager"],
  apm: ["associate", "product", "manager"],
  ml: ["machine", "learning"],
  eng: ["engineer"],
  engineering: ["engineer"],
  engineers: ["engineer"],
  developer: ["engineer"],
  internship: ["intern"],
  interns: ["intern"],
  mgr: ["manager"],
  management: ["manager"],
  sr: ["senior"],
  jr: ["junior"],
  analytics: ["analyst"],
  scientists: ["scientist"],
  science: ["scientist"],
};

const ROLE_STOPWORDS = new Set([
  "a", "an", "the", "and", "of", "for", "in", "at", "to", "on", "with",
  "summer", "fall", "winter", "spring", "autumn",
  "position", "role", "program", "opportunity", "team", "job",
]);

/** Lowercase ASCII words, "&" -> "and" (matching._ascii_words). */
function asciiWords(text: string): string[] {
  return text
    .normalize("NFKD")
    .replace(/[\u0080-\uffff]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** "Software Engineering Internship, Summer 2027" -> {software, engineer, intern} */
export function roleTokens(role?: string | null): Set<string> {
  if (!role) return new Set();
  let text = asciiWords(role).join(" ");
  for (const [pattern, replacement] of ROLE_PHRASES) text = text.replace(pattern, replacement);
  const tokens = new Set<string>();
  for (const word of text.split(" ")) {
    if (!word || ROLE_STOPWORDS.has(word) || /^(19|20)\d{2}$/.test(word)) continue;
    (ROLE_SYNONYMS[word] || [word]).forEach((t) => tokens.add(t));
  }
  return tokens;
}

const ROLE_MATCH_THRESHOLD = 0.65;
const MIN_TOKENS_FOR_CONTAINMENT = 3;

/** 0–1 similarity of two job titles; null when either is missing. */
export function roleSimilarity(a?: string | null, b?: string | null): number | null {
  const ta = roleTokens(a);
  const tb = roleTokens(b);
  if (!ta.size || !tb.size) return null;
  const shared = Array.from(ta).filter((t) => tb.has(t)).length;
  const smaller = Math.min(ta.size, tb.size);
  if (shared === smaller && smaller >= MIN_TOKENS_FOR_CONTAINMENT) return 1;
  return shared / new Set([...Array.from(ta), ...Array.from(tb)]).size;
}

export function rolesMatch(a?: string | null, b?: string | null): boolean | null {
  const similarity = roleSimilarity(a, b);
  return similarity === null ? null : similarity >= ROLE_MATCH_THRESHOLD;
}

/**
 * Open roles the user has already applied to (a tracker card with a matching
 * title). When the card says where ("Toronto"), only open roles there count,
 * so "Intern (Toronto)" isn't confused with "Intern (USA)".
 */
export function appliedRoleIds(roles: OpenRole[], applications: ApiItem[]): Set<string> {
  const applied = new Set<string>();
  for (const app of applications) {
    const candidates = roles.filter((role) => rolesMatch(role.title, app.role));
    const place = asciiWords(app.lctn || "");
    // A card that says where only counts for open roles there
    const here = place.length
      ? candidates.filter((role) => {
          const words = new Set(asciiWords([role.title, ...role.locations].join(" ")));
          return place.every((w) => words.has(w));
        })
      : candidates;
    here.forEach((role) => applied.add(role.id));
  }
  return applied;
}

/** Tracker cards for this company. */
export function applicationsFor(company: DreamCompany, cards: ApiItem[]): ApiItem[] {
  const key = normalizeCompany(company.name);
  if (!key) return [];
  return cards.filter((card) => normalizeCompany(card.company) === key);
}

// --- Progress --------------------------------------------------------------------

export type DreamStage = "research" | "connect" | "apply" | "interview";

export const DREAM_STAGES: { id: DreamStage; label: string }[] = [
  { id: "research", label: "Research" },
  { id: "connect", label: "Connect" },
  { id: "apply", label: "Apply" },
  { id: "interview", label: "Interview" },
];

// An OA isn't an interview: it keeps the bar at Apply
const INTERVIEW_STAGES = new Set(["interview", "offer"]);

/**
 * Which steps the user has reached. Each is checked on its own (you can apply
 * without networking first), except that an interview means you applied.
 */
export function stagesReached(company: DreamCompany, applications: ApiItem[]): Record<DreamStage, boolean> {
  const interview = applications.some(
    (card) =>
      INTERVIEW_STAGES.has(toStatus(card.predicted_label) ?? "") ||
      (card.history || []).some((event) => INTERVIEW_STAGES.has(event.stage))
  );
  // Applying shows up through Gmail as a tracker card
  const apply = interview || applications.length > 0;
  const connect = (company.people || []).some((person) => person.status !== "to-reach-out");
  return { research: true, connect, apply, interview };
}

// --- Deadlines -------------------------------------------------------------------

/** Whole days from today to a yyyy-mm-dd date; null if it isn't one. */
export function daysUntil(date?: string, now = new Date()): number | null {
  if (!date) return null;
  const target = new Date(date + "T00:00:00");
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

/** Soonest deadline that hasn't passed, across the roles saved by hand. */
export function soonestDeadline(company: DreamCompany, now = new Date()): number | null {
  const days = (company.roles || [])
    .map((role) => daysUntil(role.deadline, now))
    .filter((d): d is number => d !== null && d >= 0);
  return days.length ? Math.min(...days) : null;
}

// --- Order -----------------------------------------------------------------------

/** Whether we know the company's job board: still looking, none we can read, or found. */
export function trackingState(company: DreamCompany): "pending" | "untracked" | "tracked" {
  if (company.boardKey === undefined) return "pending";
  return company.boardKey ? "tracked" : "untracked";
}

/** A company still waiting for detection this long gets retried, then a "Try again". */
export const DETECT_RETRY_AFTER_MS = 60 * 1000;
export const DETECT_STALLED_AFTER_MS = 2 * 60 * 1000;

/** How long the company has been waiting for its job board check (0 if it isn't, or its age is unknown). */
export function detectionWait(company: DreamCompany, now = Date.now()): number {
  if (trackingState(company) !== "pending") return 0;
  const created = toMillis(company.createdAt);
  return created ? Math.max(0, now - created) : 0;
}

/**
 * Quiet level note for an open role: "New grad", "2+ yrs asked", "Entry level".
 * Nothing when the posting doesn't say: on most rows that would just be noise.
 */
export function roleLevelText(role: OpenRole): string {
  const years = role.yearsRequired;
  if (role.level === "new_grad") return "New grad";
  if (years != null && years >= 1) return `${years}+ yrs asked`;
  if (role.level === "open" && years === 0) return "Entry level";
  return "";
}

/** Matching open roles, if they're for the company's current board. */
export function openRoles(company: DreamCompany): OpenRole[] {
  const tracking = company.tracking;
  if (!company.boardKey || tracking?.boardKey !== company.boardKey) return [];
  return tracking.openRoles || [];
}

/** How long a role stays "new" if the user doesn't open the company. */
export const NEW_FOR_DAYS = 7;

/** New roles the user hasn't looked at yet (opened after they last checked the company, within 7 days). */
export function unseenNewRoles(company: DreamCompany, now = Date.now()): OpenRole[] {
  const seenAt = Date.parse(company.tracking?.seenAt || "") || 0;
  const cutoff = now - NEW_FOR_DAYS * 86400000;
  return openRoles(company).filter((role) => {
    const at = Date.parse(role.firstSeenAt || "") || 0;
    return role.new && at > seenAt && at > cutoff;
  });
}

/**
 * The company's open roles for its panel: the ones with the "new" dot
 * (unseenNewRoles) on top, newest first, then the rest in the board's order.
 */
export function openRolesNewFirst(company: DreamCompany, now = Date.now()): OpenRole[] {
  const seen = (role: OpenRole) => Date.parse(role.firstSeenAt || "") || 0;
  const unseen = unseenNewRoles(company, now).sort((a, b) => seen(b) - seen(a));
  const ids = new Set(unseen.map((role) => role.id));
  return [...unseen, ...openRoles(company).filter((role) => !ids.has(role.id))];
}

/** Firestore timestamp, Date or ISO string -> ms (0 if missing). */
export function toMillis(value: any): number {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  return Date.parse(value) || 0;
}

function newestUnseenAt(company: DreamCompany, now: number): number {
  const times = unseenNewRoles(company, now).map((role) => Date.parse(role.firstSeenAt || "") || 0);
  return times.length ? Math.max(...times) : 0;
}

/**
 * Changes on top: unseen new roles (newest first), then the soonest deadline,
 * then the rest. Companies we can't track go last. Ties keep the incoming order.
 */
export function compareDreamCompanies(a: DreamCompany, b: DreamCompany, now = new Date()): number {
  const newA = newestUnseenAt(a, now.getTime());
  const newB = newestUnseenAt(b, now.getTime());
  if (newA || newB) return newB - newA;

  const untrackedA = trackingState(a) === "untracked";
  const untrackedB = trackingState(b) === "untracked";
  if (untrackedA !== untrackedB) return untrackedA ? 1 : -1;

  const dueA = soonestDeadline(a, now);
  const dueB = soonestDeadline(b, now);
  if (dueA !== null && dueB !== null) return dueA - dueB;
  if (dueA !== null) return -1;
  if (dueB !== null) return 1;
  return 0;
}

// --- Display ---------------------------------------------------------------------

/** "just now", "5m ago", "2h ago", "3d ago". */
export function shortTimeAgo(iso?: string, now = Date.now()): string {
  const at = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(at)) return "";
  const mins = Math.floor((now - at) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export const plural = (n: number, word: string, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

/** "https://www.Stripe.com/careers" -> "stripe.com" */
export function cleanDomain(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0];
}
